import './AdminInvitationPage.less';
import ProTable, { ActionType } from '@ant-design/pro-table';
import { Button, Divider, Input, Select, Tag, Tooltip, Typography } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import { SorterResult } from 'antd/es/table/interface';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ContextApp } from '../../contexts/ContextApp';
import { EnumsValues } from '../../enums/EnumsValues';
import GraphqlService from '../../services/graphql/GraphqlService';
import {
  ABM,
  Authorization,
  showCollapseRender,
  Tools,
  DateTimeUtils,
} from '../../shared';
import { ExportableColumn, ExportableTable } from '../../shared/Exporter';
import { ExporterDropdown, SaveForm } from '../../components/common/ABM';
import TableSearchInput from '../../components/common/TableSearchInput';
import dayjs from 'dayjs';
import {
  LeftOutlined,
  RedoOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { FORMAT_DATE_TIME_1 } from '../../shared/DateTimeUtils';
import { useNavigate } from 'react-router-dom';
import useProTableForMobile from '../../hooks/useProTableForMobile';
import { useCustomMessage } from '../../hooks/useCustomMessage';
import {
  useCustomLazyQuery,
  useCustomMutation,
} from '../../hooks/apollo/ApolloCustomHooks';
import { tooltipTrigger } from '../../shared/antdUtils';
import { IInvitation } from '../../interfaces/Invitation';
import { useScopedMutation } from '../../hooks/useScopedMutation';
import { getRequestError } from '../../shared/requestError';
import { useTenantRoles } from '../../hooks/useTenantReferenceData';

interface InvitationQueryVariables {
  searchText?: string;
  tenant_id?: number;
  filter?: Record<string, unknown>;
  orderBy?: { direction?: string; field?: string };
}

interface InvitationMutationPayload {
  __typename?: string;
  id?: number;
  uid?: string;
  status_code?: number;
  message?: string;
  message_translation_key?: string;
}

interface InvitationTableParams extends Record<string, unknown> {
  accepted?: string;
  sorter?: Record<string, string | null | undefined>;
  filter?: Record<string, unknown>;
}

const renderEmptyTable = () => null;

const AdminInvitationPage = () => {
  const { functions, t, selectedTenantId, tenantsAssociatedToUser, user } =
    useContext(ContextApp);
  const [sorter, setSorter] = useState<string>('');
  const [searchText, setSearchText] = useState('');
  const [dataTable, setDataTable] = useState<IInvitation[]>([]);
  const [params, setParams] = useState<Record<string, unknown>>();
  const [invitationModalVisible, setInvitationModalVisible] = useState(false);
  const formSession = useMemo(() => ({}), [invitationModalVisible]);
  const { runMutation, formLoading } = useScopedMutation(
    user?.id,
    selectedTenantId,
    formSession,
  );
  const navigate = useNavigate();

  const { Query, Mutation } = GraphqlService();
  const {
    messageError,
    messageDeleteSuccess,
    messageDeleting,
    messageCreateError,
    getErrorMessage,
    messageSuccess,
    messageLoading,
    messageModalConfirm,
  } = useCustomMessage();

  const TITLE_INVITATION_FORM = t('abm.titleCreateForm', {
    replace: { entity: t('entity.invitation') },
  });
  const INPUT_SEARCH_PLACEHOLDER = t('abm.inputSearchPlaceholder');
  const LIST_FILTER = ['email', 'role_id', 'accepted'];
  const LIST_SORTER = ['email', 'due_date', 'accepted'];

  const actionRef = useRef<ActionType | undefined>(undefined);
  const variables = useRef<InvitationQueryVariables>({});
  const reloadUserCombo = useRef<boolean>(true);
  const invitationsRequest = useRef(0);
  const currentTenantId = useRef(selectedTenantId);
  currentTenantId.current = selectedTenantId;

  const rolesQuery = useTenantRoles(selectedTenantId);

  const [createInvitationMutation] = useCustomMutation<{
    createInvitation: InvitationMutationPayload;
  }>(Mutation.createInvitation);

  const [deleteInvitationMutation] = useCustomMutation<{
    deleteInvitation: InvitationMutationPayload;
  }>(Mutation.deleteInvitation);

  const [resendInvitationMutation] = useCustomMutation<{
    resendInvitation: InvitationMutationPayload;
  }>(Mutation.resendInvitation);

  const [fetchInvitations] = useCustomLazyQuery<{
    invitations: IInvitation[];
  }>(Query.invitations, {
    variables: { ...variables.current, tenant_id: selectedTenantId },
  });

  const { mobileOnSizeChangeProTable, showComponent } = useProTableForMobile({
    layout: 'horizontal',
  });

  useEffect(() => {
    invitationsRequest.current += 1;
    setInvitationModalVisible(false);
    setDataTable([]);
  }, [selectedTenantId]);

  const createInvitation = async (value: {
    role_id: number;
    tenant_id: number;
    email: string;
  }) => {
    await runMutation({
      channel: 'form',
      onStart: () =>
        messageLoading({
          context: 'adminInvitation.createInvitation.1',
          message: t('abm.message.sendingInvitation'),
        }),
      send: () => createInvitationMutation({ variables: { ...value } }),
      onSuccess: (_, formCurrent) => {
        messageSuccess({
          context: 'adminInvitation.createInvitation.2',
          message: t('abm.message.invitationSended'),
        });
        void actionRef.current?.reloadAndRest?.();
        if (formCurrent) setInvitationModalVisible(false);
      },
      onError: (error: unknown) => {
        const requestError = getRequestError(error);
        if (requestError.status_code && requestError.message) {
          messageError({
            error: requestError,
            context: 'adminInvitation.createInvitation.3',
            message: getErrorMessage(requestError),
          });
        } else {
          messageCreateError({
            error,
            context: 'adminInvitation.createInvitation.4',
          });
        }
      },
    });
  };

  const deleteConfirmedInvitation = async (id: number) => {
    await runMutation({
      channel: `delete:${id}`,
      onStart: () =>
        messageDeleting({
          context: 'adminInvitation.deleteInvitation.1',
          message: t('entity.invitation'),
        }),
      send: () =>
        deleteInvitationMutation({
          variables: { id, tenant_id: selectedTenantId },
        }),
      onSuccess: () => {
        messageDeleteSuccess({
          context: 'adminInvitation.deleteInvitation.2',
        });
        if (actionRef.current?.reset) {
          reloadUserCombo.current = true;
          actionRef.current.reset();
        }
      },
      onError: (error: unknown) =>
        messageError({
          error,
          context: 'adminInvitation.deleteInvitation.3',
          message: getErrorMessage(error),
        }),
    });
  };

  const deleteInvitation = (invitation: IInvitation) => {
    messageModalConfirm({
      context: 'adminInvitation.deleteInvitation.confirm',
      props: {
        content: t('abm.message.confirmDeleteInvitation', {
          replace: { email: invitation.email },
        }),
        cancelText: Tools.capitalize(t('action.cancel')),
        okText: Tools.capitalize(t('action.ok')),
        onOk: () => deleteConfirmedInvitation(invitation.id),
      },
    });
  };

  const resendInvitation = async (id: number) => {
    await runMutation({
      channel: `resend:${id}`,
      onStart: () =>
        messageLoading({
          context: 'adminInvitation.resendInvitation.1',
          message: t('abm.message.sendingInvitation'),
        }),
      send: () =>
        resendInvitationMutation({
          variables: { id, tenant_id: selectedTenantId },
        }),
      onSuccess: () => {
        messageSuccess({
          context: 'adminInvitation.resendInvitation.2',
          message: t('abm.message.invitationSended'),
        });
        if (actionRef.current?.reset) {
          reloadUserCombo.current = true;
          actionRef.current.reset();
        }
      },
      onError: (error: unknown) =>
        messageError({
          error,
          context: 'adminInvitation.resendInvitation.3',
          message: getErrorMessage(error),
        }),
    });
  };

  const request = async ({
    sorter = {},
    filter = {},
  }: InvitationTableParams) => {
    const requestId = ++invitationsRequest.current;
    const tenantId = selectedTenantId;
    delete variables.current.filter;
    delete variables.current.orderBy;
    variables.current = {};

    const search: Record<string, unknown> = ABM.valuesResult({
      sorter,
      filter,
      ...params,
    });
    switch (params?.accepted) {
      case 'true':
        search.accepted = true;
        break;
      case 'false':
        search.accepted = false;
        break;
      case 'null':
        search.accepted = null;
        break;
    }
    if (searchText) {
      variables.current.searchText = searchText;
    } else {
      delete variables.current.searchText;
    }

    LIST_FILTER.forEach((element) => {
      const filterValue = search[element];
      if (filterValue !== undefined) {
        variables.current.filter ??= {};
        variables.current.filter[element] = filterValue;
      }
    });

    const searchSorter = search.sorter as
      | Record<string, string | null | undefined>
      | undefined;
    LIST_SORTER.forEach((element) => {
      const sortDirection = searchSorter?.[element];
      if (sortDirection) {
        variables.current.orderBy = {
          direction: Tools.getTypeOrderByTableSortParam(sortDirection),
          field: element,
        };
      }
    });
    try {
      const { data, error } = await fetchInvitations({
        variables: { ...variables.current, tenant_id: tenantId },
      });

      if (error) {
        throw error;
      }
      if (
        requestId === invitationsRequest.current &&
        tenantId === currentTenantId.current
      ) {
        setDataTable(() => data?.invitations ?? []);
        if (reloadUserCombo.current) {
          reloadUserCombo.current = false;
        }
      }
      return {
        current: 1,
        data: data?.invitations,
        pageSize: '1',
        success: true,
        total: data?.invitations?.length,
      };
    } catch (error) {
      if (
        requestId === invitationsRequest.current &&
        tenantId === currentTenantId.current
      ) {
        messageError({
          error,
          context: 'adminInvitation.request.1',
          message: getErrorMessage(error),
        });
      }
      return {
        current: 1,
        data: [],
        pageSize: '1',
        success: false,
        total: 0,
      };
    }
  };

  const invitationColumns = useCallback(
    (): ExportableColumn[] => [
      {
        export: false,
        dataIndex: 'tenant_id',
        title: Tools.capitalize(t('entity.tenant')),
        renderFormItem: () => (
          <Select
            disabled
            value={selectedTenantId}
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
            options={tenantsAssociatedToUser.map((tenant) => ({
              label: tenant.name,
              value: tenant.id,
            }))}
            placeholder={t('abm.selectPlaceholder', {
              replace: { entity: t('entity.tenant') },
            })}
            maxLength={200}
          />
        ),
        align: 'left',
        type: ABM.TYPE_COLUMN.NUMBER,
      },
      {
        export: false,
        dataIndex: 'email',
        title: Tools.capitalize(t('entity.email')),
        formItemProps: {
          rules: [
            {
              required: true,
              message: t('validation.emailRequired'),
            },
            {
              type: 'email',
              message: t('validation.emailInvalid'),
            },
          ],
        },
        renderFormItem: () => (
          <Input
            placeholder={t('abm.inputPlaceholder', {
              replace: { entity: t('entity.email') },
            })}
            maxLength={200}
          />
        ),
        align: 'left',
      },
      {
        export: false,
        dataIndex: 'role_id',
        title: Tools.capitalize(t('entity.role')),
        valueType: 'select',
        formItemProps: {
          rules: [
            {
              required: true,
              message: t('validation.roleRequired'),
            },
          ],
        },
        renderFormItem: () => (
          <Select
            options={rolesQuery.roles.map((rol) => ({
              label: rol.name,
              value: rol.id,
            }))}
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
            placeholder={t('abm.inputPlaceholder', {
              replace: { entity: t('entity.role') },
            })}
            allowClear
            showSearch
            filterOption={(inputValue, option?: DefaultOptionType) => {
              const label = option?.label;
              return (
                typeof label === 'string' &&
                label.toLowerCase().includes(inputValue.toLowerCase())
              );
            }}
          />
        ),
      },
    ],
    [rolesQuery.roles],
  );

  const columns = useCallback(
    (): ExportableColumn<IInvitation>[] => [
      {
        export: false,
        dataIndex: 'idUser',
        title: Tools.capitalize(t('entity.id')),
        hideInTable: true,
        hideInSearch: true,
        hideInForm: true,
        type: ABM.TYPE_COLUMN.NUMBER,
      },
      {
        export: true,
        dataIndex: 'email',
        title: Tools.capitalize(t('entity.email')),
        render: (_, record) => record.email,
        renderFormItem: () => (
          <Input
            placeholder={t('abm.inputPlaceholder', {
              replace: { entity: t('entity.email') },
            })}
            maxLength={200}
          />
        ),
        align: 'left',
        hideInTable: false,
        hideInSearch: false,
        hideInForm: true,
      },
      {
        export: true,
        dataIndex: 'role_id',
        title: Tools.capitalize(t('entity.role')),
        valueType: 'select',
        render: (_, record) =>
          record.role?.name ? <Tag>{record.role.name}</Tag> : '-',
        renderFormItem: () => (
          <Select
            options={rolesQuery.roles.map((rol) => ({
              label: rol.name,
              value: rol.id,
            }))}
            placeholder={t('abm.inputPlaceholder', {
              replace: { entity: t('entity.role') },
            })}
            mode="multiple"
            allowClear
            showSearch
            filterOption={(inputValue, option?: DefaultOptionType) => {
              const label = option?.label;
              return (
                typeof label === 'string' &&
                label.toLowerCase().includes(inputValue.toLowerCase())
              );
            }}
          />
        ),
        align: 'left',
        hideInTable: false,
        hideInSearch: false,
        hideInForm: true,
      },
      {
        export: true,
        dataIndex: 'due_date',
        title: Tools.capitalize(t('entity.expirationDate')),
        render: (_, record) =>
          dayjs(record.due_date).format(FORMAT_DATE_TIME_1),
        align: 'left',
        hideInTable: false,
        hideInSearch: true,
        hideInForm: true,
      },
      {
        export: true,
        dataIndex: 'accepted',
        title: Tools.capitalize(t('entity.status')),
        type: ABM.TYPE_COLUMN.BOOLEAN,
        render: (_, record) => {
          let status: { color: 'warning' | 'success' | 'error'; label: string };
          if (record.accepted === null) {
            status = { color: 'warning', label: t('abm.invitationPending') };
          } else if (record.accepted) {
            status = { color: 'success', label: t('abm.invitationAccepted') };
          } else {
            status = { color: 'error', label: t('abm.invitationRejected') };
          }
          return <Tag color={status.color}>{status.label}</Tag>;
        },
        renderFormItem: () => (
          <Select
            options={[
              { label: t('abm.invitationAccepted'), value: 'true' },
              { label: t('abm.invitationRejected'), value: 'false' },
              { label: t('abm.invitationPending'), value: 'null' },
            ]}
            allowClear
          />
        ),
        align: 'left',
        hideInTable: false,
        hideInSearch: false,
        hideInForm: true,
      },
      {
        title: Tools.capitalize(t('abm.operations')),
        dataIndex: 'option',
        valueType: 'option',
        fixed: 'right',
        width: 100,
        export: false,
        hideInTable: false,
        hideInSearch: true,
        hideInForm: true,
        render: (_, record) => (
          <>
            {Authorization.security(
              functions,
              EnumsValues.Functions.UserCreate,
            ) ? (
              <Tooltip
                key="remove_user_tooltip"
                title={t('abm.resendInvitation')}
              >
                <Button
                  className="pointer"
                  onClick={() => {
                    void resendInvitation(record.id);
                  }}
                  disabled={record.accepted !== null}
                  icon={<RedoOutlined />}
                  type="text"
                />
              </Tooltip>
            ) : null}
            {Authorization.security(
              functions,
              EnumsValues.Functions.UserCreate,
            ) ? (
              <>
                <Divider type="vertical" />
                <Tooltip
                  key="delete_invitation_tooltip"
                  trigger={tooltipTrigger}
                  title={t('abm.deleteTooltip', {
                    replace: { entity: t('entity.invitation') },
                  })}
                >
                  <DeleteOutlined
                    className="pointer"
                    onClick={() => {
                      deleteInvitation(record);
                    }}
                  />
                </Tooltip>
              </>
            ) : null}
          </>
        ),
      },
    ],
    [rolesQuery.roles, resendInvitation, deleteInvitation, functions],
  );

  const LIST_FILTER_NAMES = columns()
    .filter(
      (value) =>
        LIST_FILTER.some(
          (element) =>
            element === value.dataIndex && value.hideInTable === false,
        ) && typeof value.title === 'string',
    )
    .map((element) => element.title)
    .filter((title): title is string => typeof title === 'string');

  const handleSearch = (value: string) => {
    setSearchText(value);
    if (actionRef.current?.reloadAndRest) {
      void actionRef.current.reloadAndRest();
    }
  };

  const exportableTable: ExportableTable<IInvitation> = {
    columns: columns(),
    items: dataTable,
    filename: `invitations_${DateTimeUtils.momentDefaultFormat()}`,
  };

  useEffect(() => {
    void actionRef.current?.reloadAndRest?.();
  }, [params]);

  return (
    <main className="invitation-container" data-testid="AdminInvitationPage">
      <Button
        className="invitation-page__back"
        type="text"
        icon={<LeftOutlined />}
        onClick={() => {
          navigate(-1);
        }}
      >
        {Tools.capitalize(t('action.back'))}
      </Button>
      <header className="invitation-page__intro">
        <Typography.Title level={1}>
          {t('invitationPage.title')}
        </Typography.Title>
        <Typography.Paragraph>
          {t('invitationPage.description')}
        </Typography.Paragraph>
      </header>
      <section
        className="invitation-page__filters"
        aria-label={t('action.search')}
      >
        <ProTable
          headerTitle={null}
          rowKey="id"
          search={{
            resetText: Tools.capitalize(t('action.clean')),
            searchText: Tools.capitalize(t('action.search')),
            collapseRender: showCollapseRender(columns()),
            layout: 'horizontal',
          }}
          request={(params) => {
            setParams({ ...params });
            return Promise.resolve({ data: [], success: true });
          }}
          columns={columns()}
          tableRender={renderEmptyTable}
        />
      </section>
      <ProTable<IInvitation>
        className="invitation-page__table"
        key={selectedTenantId}
        onSizeChange={mobileOnSizeChangeProTable}
        components={{
          table: showComponent(),
        }}
        headerTitle={null}
        actionRef={actionRef}
        rowKey="id"
        search={false}
        onChange={(_, _filter, _sorter) => {
          const sorterResult = _sorter as SorterResult<IInvitation>;
          if (sorterResult.field) {
            setSorter(`${sorterResult.field}_${sorterResult.order}`);
          }
        }}
        onReset={() => {
          setSearchText('');
        }}
        params={{
          sorter,
          tenantId: selectedTenantId,
        }}
        toolBarRender={() => []}
        toolbar={{
          search: (
            <div className="content-search-table">
              <TableSearchInput
                value={searchText}
                placeholder={INPUT_SEARCH_PLACEHOLDER}
                tooltip={
                  Tools.capitalize(t('action.searchBy')) +
                  LIST_FILTER_NAMES.join(', ')
                }
                resetKey={selectedTenantId}
                onSearch={handleSearch}
              />
            </div>
          ),
          actions: [
            <div
              className="invitation-page__toolbar-actions"
              key="invitation-actions"
            >
              {Authorization.security(
                functions,
                EnumsValues.Functions.UserCreate,
              ) ? (
                <Button
                  type="primary"
                  onClick={() => {
                    setInvitationModalVisible(true);
                  }}
                  icon={<PlusOutlined />}
                >
                  {Tools.capitalize(t('abm.invite'))}
                </Button>
              ) : null}
              <ExporterDropdown exportable={exportableTable} />
            </div>,
          ],
        }}
        /**
         * @description este metodo debe poder ejecutar siempre la consulta al backend
         */
        request={async (params, sorter, filter) =>
          request({ ...params, sorter, filter })
        }
        columns={columns()}
      />
      <SaveForm
        loading={formLoading}
        title={TITLE_INVITATION_FORM}
        onCancel={() => {
          setInvitationModalVisible(false);
        }}
        modalVisible={invitationModalVisible}
        onOk={(value: Parameters<typeof createInvitation>[0]) =>
          createInvitation(value)
        }
        columns={invitationColumns()}
        values={{
          tenant_id: selectedTenantId,
        }}
        cancelText={Tools.capitalize(t('action.clean'))}
        submitText={Tools.capitalize(t('action.send'))}
      />
    </main>
  );
};

export default AdminInvitationPage;
